import { useState } from "react";
import { motion } from "framer-motion";
import { X, Globe, MapPin, EnvelopeSimple, Phone, Buildings } from "@phosphor-icons/react";

export default function ProspectForm({ onSubmit, onClose }) {
  const [form, setForm] = useState({
    name: "",
    website: "",
    email: "",
    phone: "",
    address: "",
    zip_code: "",
    city: "",
    state: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.zip_code.trim()) return;
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white w-full max-w-lg border border-[--border] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="prospect-form-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[--border]">
          <div>
            <h2
              className="text-xl font-bold tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Add Prospect
            </h2>
            <p className="text-sm text-[--text-muted] mt-0.5">
              Enter the chiropractor practice details
            </p>
          </div>
          <button
            data-testid="close-form-btn"
            onClick={onClose}
            className="p-2 hover:bg-[--bg-muted] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <FormField
            icon={Buildings}
            name="name"
            label="Practice Name"
            value={form.name}
            onChange={handleChange}
            placeholder="Smith Family Chiropractic"
            required
          />
          <FormField
            icon={Globe}
            name="website"
            label="Website URL"
            value={form.website}
            onChange={handleChange}
            placeholder="www.smithchiro.com"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              icon={EnvelopeSimple}
              name="email"
              label="Email"
              value={form.email}
              onChange={handleChange}
              placeholder="info@smithchiro.com"
            />
            <FormField
              icon={Phone}
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </div>
          <FormField
            icon={MapPin}
            name="address"
            label="Address"
            value={form.address}
            onChange={handleChange}
            placeholder="123 Main St"
          />
          <div className="grid grid-cols-3 gap-4">
            <FormField
              name="city"
              label="City"
              value={form.city}
              onChange={handleChange}
              placeholder="Austin"
            />
            <FormField
              name="state"
              label="State"
              value={form.state}
              onChange={handleChange}
              placeholder="TX"
            />
            <FormField
              icon={MapPin}
              name="zip_code"
              label="ZIP Code"
              value={form.zip_code}
              onChange={handleChange}
              placeholder="78701"
              required
            />
          </div>

          <div className="pt-2">
            <button
              data-testid="submit-prospect-btn"
              type="submit"
              disabled={submitting || !form.name.trim() || !form.zip_code.trim()}
              className="w-full bg-[--primary] text-white py-3 font-medium text-sm hover:bg-[--primary-hover] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Adding..." : "Add Prospect"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function FormField({ icon: Icon, name, label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.12em] text-[--text-muted] mb-1.5 block">
        {label} {required && <span className="text-[--danger]">*</span>}
      </label>
      <div className="flex items-center gap-2 px-3 py-2.5 border border-[--border] bg-[--bg-hover] focus-within:border-[--primary] focus-within:ring-1 focus-within:ring-[--primary] transition-all">
        {Icon && <Icon size={16} className="text-[--text-muted] flex-shrink-0" />}
        <input
          data-testid={`form-${name}-input`}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="flex-1 bg-transparent outline-none text-sm text-[--text-primary] placeholder:text-[--text-muted]"
        />
      </div>
    </div>
  );
}
